import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ValidationError } from "yup";

import { tollFreeRegistrationSchema } from "@stw/yup-validators";

import {
  ComplianceRegistrationUsageType,
  CreateTollFreeRegistrationDraftMutation,
  CreateTollFreeRegistrationDraftMutationVariables,
  EditTollFreeRegistrationDraftMutation,
  EditTollFreeRegistrationDraftMutationVariables,
  GetTollFreeRegistrationForAdminQuery,
  GetTollFreeRegistrationForAdminQueryVariables,
  SubmitTollFreeRegistrationToStaffMutation,
  SubmitTollFreeRegistrationToStaffMutationVariables,
  TollFreeOrgType,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { Checkbox } from "~frontend/components/Checkbox";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink, LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import {
  FormikErrorBanner,
  FormikSelect,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import { FormikSearchableSelect } from "~frontend/components/forms/FormSelect";
import {
  CREATE_TOLL_FREE_REGISTRATION_DRAFT,
  EDIT_TOLL_FREE_REGISTRATION_DRAFT,
  GET_TOLL_FREE_REGISTRATION_FOR_ADMIN,
  SUBMIT_TOLL_FREE_REGISTRATION_TO_STAFF,
} from "~frontend/graphql/tollFreeRegistrations";
import {
  useCurrentUser,
  useCurrentUserIsStaff,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { InputRow } from "~frontend/routes/staff/compliance-registrations/ten-dlc/TenDlcProvider";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";

import useOptOutPhraseList from "../campaignEditor/Script/useOptOutPhraseList";

const ORG_TYPE_MENU_OPTIONS: { label: string; value: TollFreeOrgType }[] = [
  {
    label: "527 Organization (Political Campaign, Party, or PAC)",
    value: TollFreeOrgType.Political,
  },
  {
    label: "501c3",
    value: TollFreeOrgType.C3,
  },
  {
    label: "501c4",
    value: TollFreeOrgType.C4,
  },
  {
    label: "501c5",
    value: TollFreeOrgType.C5,
  },
  {
    label: "Private Entity",
    value: TollFreeOrgType.Private,
  },
  {
    label: "Government",
    value: TollFreeOrgType.Government,
  },
];

export const COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS: {
  label: string;
  value: ComplianceRegistrationUsageType;
}[] = [
  {
    label: "Dialer",
    value: ComplianceRegistrationUsageType.Dialer,
  },
  {
    label: "Texter",
    value: ComplianceRegistrationUsageType.Texter,
  },
];

export const InternalTollFreeRegistrationForm = ({
  registration,
  handleSubmitToStaff,
}: {
  registration?: GetTollFreeRegistrationForAdminQuery["getTollFreeRegistrationForAdmin"];
  handleSubmitToStaff: (
    values: Omit<
      SubmitTollFreeRegistrationToStaffMutationVariables["input"],
      "subaccountId"
    >
  ) => Promise<{
    errors: Error[] | string[] | undefined | null;
  }>;
}): ReactElement => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  const { subaccount, subaccountLink } = useSubaccount() ?? {};
  const allowsDraftSave = !!subaccount;
  const isReadOnly = !!registration?.submittedToStaffAt;

  const [createTollFreeRegistrationDraft] = useMutation<
    CreateTollFreeRegistrationDraftMutation,
    CreateTollFreeRegistrationDraftMutationVariables
  >(CREATE_TOLL_FREE_REGISTRATION_DRAFT, {
    onErrorSnackbarMessage: "Failed to create toll free registration",
  });

  const [editTollFreeRegistrationDraft] = useMutation<
    EditTollFreeRegistrationDraftMutation,
    EditTollFreeRegistrationDraftMutationVariables
  >(EDIT_TOLL_FREE_REGISTRATION_DRAFT, {
    onErrorSnackbarMessage: "Failed to update toll free registration",
  });

  const { phraseList: optOutPhrases, loading: optOutPhrasesLoading } =
    useOptOutPhraseList();

  const currentUser = useCurrentUser();
  const isStaff = useCurrentUserIsStaff();
  const isProd = import.meta.env.STW_ENV_NAME === "production";

  const allowTestPresetUse = isStaff && !isProd;

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmittingToStaff, setIsSubmittingToStaff] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [testPreset, setTestPreset] = useState<string>("default");
  const TEST_PRESETS = [
    {
      label: "Test Preset 1: Political Organization",
      value: "test-preset-1",
      data: {
        organizationName: `${currentUser?.fullName || "Test"}'s Test Organization`,
        orgType: TollFreeOrgType.Political,
        displayName: `${currentUser?.fullName || "Test"}'s Test Org`,
        ein: "00-0000001",
        cvToken:
          "cv|1.0|mno|tfree|b3419fb8-d68c-4d23-bdcc-6a05c81a319a|aBcDeFg",
        address1: "123 Street St",
        address2: "",
        city: "City",
        state: "AK",
        postalCode: "10025",
        country: "US",
        website: "https://www.scaletowin.com",

        contactFirstName: `${currentUser?.fullName.split(" ")[0] || "Test"}`,
        contactLastName: `${currentUser?.fullName.split(" ").slice(1).join(" ") || "User"}`,
        contactEmail: currentUser?.email || "test@scaletowin.com",
        contactPhone: "212-555-1212",

        orgContactIsPrimaryContact: true,

        primaryContactFirstName: "",
        primaryContactLastName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",

        createComplianceBundle: true,
        acknowledgedCompliance: true,

        messageVolume: 100,
        useCaseSummary: "Sending some texts!",
        productionMessageContent: "Hello there! Stop to quit",
        usageTypes: [ComplianceRegistrationUsageType.Texter],
        privacyPolicyUrl: "scaletowin.com/privacy",
        optInPageUrl: "scaletowin.com/signup",
        termsAndConditionsUrl: "scaletowin.com/terms",

        notes: "Some notes here",
        [submitErrorField]: null,
      },
    },
    {
      label: "Test Preset 2: Private Organization",
      value: "test-preset-2",
      data: {
        organizationName: `${currentUser?.fullName || "Test"}'s Test Organization`,
        orgType: TollFreeOrgType.Private,
        displayName: `${currentUser?.fullName || "Test"}'s Test Org`,
        ein: "00-0000001",
        cvToken: "",
        address1: "123 Street St",
        address2: "",
        city: "City",
        state: "AK",
        postalCode: "10025",
        country: "US",
        website: "https://www.scaletowin.com",

        contactFirstName: `${currentUser?.fullName.split(" ")[0] || "Test"}`,
        contactLastName: `${currentUser?.fullName.split(" ").slice(1).join(" ") || "User"}`,
        contactEmail: currentUser?.email || "test@scaletowin.com",
        contactPhone: "212-555-1212",

        orgContactIsPrimaryContact: true,

        primaryContactFirstName: "",
        primaryContactLastName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",

        createComplianceBundle: true,
        acknowledgedCompliance: true,

        messageVolume: 100,
        useCaseSummary: "Sending some texts!",
        productionMessageContent: "Hello there! Stop to quit",
        usageTypes: [
          ComplianceRegistrationUsageType.Texter,
          ComplianceRegistrationUsageType.Dialer,
        ],
        privacyPolicyUrl: "scaletowin.com/privacy",
        optInPageUrl: "scaletowin.com/signup",
        termsAndConditionsUrl: "scaletowin.com/terms",

        notes: "Some notes here",
        [submitErrorField]: null,
      },
    },
  ];

  if (optOutPhrasesLoading) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <PageHeader>Toll-Free Registration</PageHeader>
      <Typography variant="display2" component="div" sx={{ pb: 2 }}>
        Read about the form requirements{" "}
        <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
          here
        </ExternalLink>
        . Have questions?{" "}
        <ExternalLink href="https://support.scaletowin.com/contact">
          Feel free to reach out to us!
        </ExternalLink>
      </Typography>
      <AccentBox sx={{ px: 10, py: 5 }}>
        <Formik
          initialValues={
            isReadOnly && registration.originalValues
              ? registration.originalValues
              : {
                  organizationName: registration?.organizationName ?? "",
                  orgType: registration?.orgType ?? "",
                  displayName: registration?.displayName ?? "",
                  ein: registration?.ein ?? "",
                  cvToken: registration?.cvToken ?? "",
                  address1: registration?.address1 ?? "",
                  address2: registration?.address2 ?? "",
                  city: registration?.city ?? "",
                  state: registration?.state ?? "",
                  postalCode: registration?.postalCode ?? "",
                  country: registration?.country ?? "US",
                  website: registration?.website ?? "",
                  contactFirstName: registration?.contactFirstName ?? "",
                  contactLastName: registration?.contactLastName ?? "",
                  contactEmail: registration?.contactEmail ?? "",
                  contactPhone: registration?.contactPhone ?? "",
                  orgContactIsPrimaryContact:
                    registration?.orgContactIsPrimaryContact ?? true,
                  primaryContactFirstName:
                    registration?.primaryContactFirstName ?? "",
                  primaryContactLastName:
                    registration?.primaryContactLastName ?? "",
                  primaryContactEmail: registration?.primaryContactEmail ?? "",
                  primaryContactPhone: registration?.primaryContactPhone ?? "",
                  createComplianceBundle:
                    registration?.createComplianceBundle ?? true,
                  acknowledgedCompliance:
                    registration?.acknowledgedCompliance ?? false,
                  messageVolume: registration?.messageVolume ?? 100,
                  useCaseSummary: registration?.useCaseSummary ?? "",
                  productionMessageContent:
                    registration?.productionMessageContent ?? "",
                  usageTypes:
                    registration?.usageTypes ??
                    ([] as ComplianceRegistrationUsageType[]),
                  privacyPolicyUrl: registration?.privacyPolicyUrl ?? "",
                  optInPageUrl: registration?.optInPageUrl ?? "",
                  termsAndConditionsUrl:
                    registration?.termsAndConditionsUrl ?? "",
                  notes: registration?.notes ?? "",
                  [submitErrorField]: null,
                }
          }
          validationSchema={tollFreeRegistrationSchema(
            optOutPhrases ?? [],
            !allowsDraftSave || hasAttemptedSubmit // When to use the required-fields schema
          )}
          onSubmit={() => undefined}
        >
          {({
            values,
            touched,
            dirty,
            setValues,
            setFieldValue,
            setErrors,
            setTouched,
            errors,
            resetForm,
            handleBlur,
          }) => {
            async function submitToStaff() {
              setIsSubmittingToStaff(true);
              try {
                let validatedValues;
                try {
                  validatedValues = await tollFreeRegistrationSchema(
                    optOutPhrases ?? [],
                    true // Schema with required fields
                  ).validate(values, {
                    stripUnknown: true,
                    abortEarly: false,
                  });
                } catch (e) {
                  if (e instanceof ValidationError) {
                    const fieldErrors: Record<string, string> = {};
                    const fieldTouched: Record<string, boolean> = {};
                    for (const err of e.inner) {
                      if (err.path) {
                        fieldErrors[err.path] = err.message;
                        fieldTouched[err.path] = true;
                      }
                    }
                    setHasAttemptedSubmit(true);
                    setErrors(fieldErrors);
                    void setTouched(fieldTouched, false);
                  }
                  snackbar.enqueueSnackbar(
                    "Please fill in all required fields before submitting",
                    { variant: "error" }
                  );
                  return;
                }

                const { errors } = await handleSubmitToStaff({
                  ...validatedValues,
                  orgType: validatedValues.orgType as TollFreeOrgType,
                  usageTypes: validatedValues.usageTypes as
                    | ComplianceRegistrationUsageType[]
                    | undefined,
                  registrationId: registration?.id ?? "",
                } as Omit<
                  SubmitTollFreeRegistrationToStaffMutationVariables["input"],
                  "subaccountId"
                >);

                if (errors) {
                  snackbar.enqueueSnackbar("Error submitting registration", {
                    variant: "error",
                  });
                } else {
                  snackbar.enqueueSnackbar(
                    "Registration submitted for review",
                    {
                      variant: "success",
                    }
                  );
                  if (subaccountLink) {
                    await navigate(subaccountLink(`/phone-numbers`));
                  }
                }
              } finally {
                setIsSubmittingToStaff(false);
              }
            }

            async function saveDraft() {
              setIsSavingDraft(true);
              try {
                if (!allowsDraftSave) {
                  return;
                }
                const validatedValues = await tollFreeRegistrationSchema(
                  optOutPhrases ?? [],
                  false // Schema without required fields
                ).validate(values, {
                  stripUnknown: true,
                  abortEarly: false,
                });
                const input = {
                  ...validatedValues,
                  orgType: validatedValues.orgType as
                    | TollFreeOrgType
                    | null
                    | undefined,
                  usageTypes: validatedValues.usageTypes as
                    | ComplianceRegistrationUsageType[]
                    | undefined,
                };
                if (registration) {
                  const { errors } = await editTollFreeRegistrationDraft({
                    variables: {
                      input: {
                        ...input,
                        subaccountId: subaccount.id,
                        registrationId: registration.id,
                      },
                    },
                  });
                  if (errors) {
                    snackbar.enqueueSnackbar("Error saving draft", {
                      variant: "error",
                    });
                  } else {
                    snackbar.enqueueSnackbar("Draft saved", {
                      variant: "success",
                    });
                    resetForm({ values });
                  }
                } else {
                  const { data: createData, errors } =
                    await createTollFreeRegistrationDraft({
                      variables: {
                        input: { ...input, subaccountId: subaccount.id },
                      },
                    });
                  if (errors) {
                    snackbar.enqueueSnackbar("Error saving draft", {
                      variant: "error",
                    });
                  } else {
                    snackbar.enqueueSnackbar("Draft saved", {
                      variant: "success",
                    });
                    const newId =
                      createData?.createTollFreeRegistrationDraft.id;
                    if (newId && subaccountLink) {
                      await navigate(
                        subaccountLink(
                          `/phone-numbers/toll-free-registration/${newId}`
                        ),
                        { state: { bypassRouteLeavingGuard: true } }
                      );
                    }
                  }
                }
              } catch (e) {
                if (e instanceof ValidationError) {
                  snackbar.enqueueSnackbar("Please fix errors before saving", {
                    variant: "error",
                  });
                }
              } finally {
                setIsSavingDraft(false);
              }
            }

            return (
              <>
                <RouteLeavingGuard when={dirty} />

                <FlexBox flexDirection="column" sx={{ gap: 1 }}>
                  <FormikErrorBanner />

                  {allowTestPresetUse && !isReadOnly ? (
                    <InputRow>
                      <div />
                      <Box sx={{ mb: 2 }}>
                        <InputLabel variant="standard" id="test-preset-label">
                          Test Presets
                        </InputLabel>
                        <Select
                          fullWidth
                          labelId="test-preset-label"
                          value={testPreset}
                          onChange={async e => {
                            const selectedPreset = TEST_PRESETS.find(
                              preset => preset.value === e.target.value
                            );
                            if (selectedPreset?.data) {
                              setTestPreset(e.target.value);
                              await setValues(selectedPreset.data);
                            }
                          }}
                        >
                          <MenuItem value="default">
                            Select a test preset
                          </MenuItem>
                          {TEST_PRESETS.map(preset => (
                            <MenuItem key={preset.value} value={preset.value}>
                              {preset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </InputRow>
                  ) : (
                    <div />
                  )}

                  <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                    Message Sender Information
                  </Typography>

                  <InputRow>
                    <FormikTextField
                      label="Organization Name"
                      name="organizationName"
                      fast
                      disabled={isReadOnly}
                    />
                    <FormikSelect
                      label="Organization Type"
                      name="orgType"
                      withNoneOption
                      fast
                      disabled={isReadOnly}
                    >
                      {ORG_TYPE_MENU_OPTIONS.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </FormikSelect>
                  </InputRow>
                  <InputRow>
                    <FormikTextField
                      label="Doing Business As (DBA) (optional)"
                      name="displayName"
                      fast
                      disabled={isReadOnly}
                    />
                    <FormikTextField
                      label={"EIN"}
                      name="ein"
                      fast
                      disabled={isReadOnly}
                      onBlur={async e => {
                        const numsOnly = e.target.value.replace("-", "");
                        const newValue = numsOnly
                          ? `${numsOnly.slice(0, 2)}-${numsOnly.slice(2)}`
                          : "";
                        await setFieldValue("ein", newValue);
                        handleBlur(e);
                      }}
                    />
                  </InputRow>
                  {values.orgType === "POLITICAL" && (
                    <InputRow>
                      <FormikTextField
                        label="Campaign Verify Token"
                        name="cvToken"
                        fast
                        disabled={isReadOnly}
                        multiline
                        minRows={3}
                        onBlur={async e => {
                          await setFieldValue("cvToken", e.target.value.trim());
                          handleBlur(e);
                        }}
                      />
                      <Typography
                        component="div"
                        sx={{
                          width: "48%",
                          alignSelf: "center",
                        }}
                      >
                        527s (political committees) must have an identity token
                        from Campaign Verify to apply.{" "}
                        <ExternalLink href="https://support.scaletowin.com/campaign-verify-registration-guide">
                          Click here to learn more about how to get verified
                          through Campaign Verify.
                        </ExternalLink>
                      </Typography>
                    </InputRow>
                  )}

                  <InputRow>
                    <FormikTextField
                      label="Street One"
                      name="address1"
                      fast
                      disabled={isReadOnly}
                    />
                    <FormikTextField
                      label="Street Two (optional)"
                      name="address2"
                      fast
                      disabled={isReadOnly}
                    />
                  </InputRow>

                  <FlexBox gap={4} sx={{ width: "100%" }}>
                    <FormikTextField
                      label="City"
                      name="city"
                      fast
                      fullWidth
                      sx={{ flexGrow: 1 }}
                      disabled={isReadOnly}
                    />
                    <FormikSearchableSelect
                      label="State"
                      name="state"
                      fast
                      options={STATE_MENU_OPTIONS.map(option => {
                        return { label: option.label, id: option.value };
                      })}
                      fullWidth
                      disabled={isReadOnly}
                      sx={{
                        flexGrow: 1,
                        ".MuiInputBase-root:focus, .MuiInputBase-root:focus-within":
                          {
                            padding: 0,
                          },
                      }}
                    />
                    <FormikTextField
                      label="Postal Code"
                      name="postalCode"
                      fast
                      fullWidth
                      sx={{ flexGrow: 1 }}
                      disabled={isReadOnly}
                    />
                  </FlexBox>

                  <Divider sx={{ pt: 2, mb: 3 }} />
                  <Box>
                    <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                      Website Requirements
                    </Typography>

                    <Typography variant="display2">
                      In order to qualify for toll-free, you must have:
                    </Typography>
                    <ul>
                      <li>
                        <Typography variant="display2">
                          An{" "}
                          <Typography variant="display2Bold">
                            active website.
                          </Typography>{" "}
                          Websites can be social media pages. Google Forms and
                          ActBlue pages are no longer accepted as websites.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="display2">
                          An{" "}
                          <Typography variant="display2Bold">
                            opt in form
                          </Typography>{" "}
                          where users can sign up to receive texts from you
                          (this can be on your main website or its own webpage).
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="display2">
                          A{" "}
                          <Typography variant="display2Bold">
                            privacy policy
                          </Typography>{" "}
                          linked to the opt in form.
                        </Typography>
                      </li>
                    </ul>

                    <Typography variant="display2">
                      Read about the requirements{" "}
                      <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
                        here.
                      </ExternalLink>{" "}
                      Unless these elements are compliant with carrier policies,
                      your registration will be rejected.
                    </Typography>
                  </Box>
                  <FormikTextField
                    name="website"
                    label="Website"
                    fast
                    disabled={isReadOnly}
                  />

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="acknowledgedCompliance"
                          value={values.acknowledgedCompliance}
                          checked={!!values.acknowledgedCompliance}
                          disabled={isReadOnly}
                          onChange={async e => {
                            await setFieldValue(
                              "acknowledgedCompliance",
                              !!e.target.checked
                            );
                          }}
                        />
                      }
                      label={
                        <Typography variant="display2Bold" sx={{ pt: 1 }}>
                          By checking this box, you acknowledge that you are
                          required to have a text message opt in form, website
                          terms & conditions and privacy policy that adheres to,
                          or is substantially similar to the toll-free
                          registration requirements.
                        </Typography>
                      }
                    />
                    {touched.acknowledgedCompliance &&
                      errors.acknowledgedCompliance && (
                        <Typography sx={{ color: palette.red }}>
                          {errors.acknowledgedCompliance}
                        </Typography>
                      )}
                  </FormGroup>

                  <Typography variant="display2" sx={{ pt: 1 }}>
                    Would you like Scale to Win to generate sample sign-up,
                    terms & conditions and privacy policy pages that meet these
                    requirements for you and submit them on your behalf?
                  </Typography>

                  <RadioGroup
                    row={true}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                    }}
                    value={values.createComplianceBundle}
                    onChange={async e => {
                      await setFieldValue(
                        "createComplianceBundle",
                        e.target.value === "true"
                      );
                    }}
                    name="createComplianceBundle"
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Yes"
                      disabled={isReadOnly}
                    />

                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="No"
                      disabled={isReadOnly}
                    />
                  </RadioGroup>
                </FlexBox>

                {!values.createComplianceBundle && (
                  <Box sx={{ py: 2, fontSize: "16px" }}>
                    <CollapsibleDrawer
                      title="Registration Requirements"
                      startOpen
                      variant="slim"
                      sxHeader={{
                        backgroundColor: palette.midGray,
                        py: 0.5,
                        px: 2,
                      }}
                      sxHeading={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: palette.navy,
                      }}
                    >
                      <FlexBox
                        direction="column"
                        gap={3}
                        sx={{
                          border: `1px solid ${palette.midGray}`,
                          background: palette.lightestGray,
                          p: 3,
                        }}
                      >
                        <Box>
                          <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                            Website Requirements
                          </Typography>
                          Your website:
                          <ul>
                            <li>Must be active (publicly accessible).</li>
                            <li>Must have content.</li>
                            <li>Must have a privacy policy.</li>
                            <li>Must have terms and conditions.</li>
                            <li>
                              Can be social media page. Google Forms and ActBlue
                              pages are no longer accepted as websites.
                            </li>
                          </ul>
                          <Box>
                            If contacts can sign up for texts from you on a
                            website, <u>it must have these elements</u>.
                          </Box>
                          <ul>
                            <li>
                              An optional field for users to enter their phone
                              number
                            </li>
                            <li>
                              Explanatory language beneath, i.e. “By providing
                              your phone number you are agreeing to receive
                              texts at a maximum of X times a WEEK/DAY/MONTH
                              from ORGANIZATION. Message and data rates may
                              apply. Text HELP for more information. Text STOP
                              to stop receiving messages. Terms [LINK] and
                              Privacy [LINK]
                            </li>
                          </ul>
                          <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
                            Read more about carrier requirements here.
                          </ExternalLink>
                        </Box>

                        <Box>
                          <Typography variant="h2SemiBold">
                            Terms and Conditions Requirements
                          </Typography>

                          <ul>
                            <li>
                              Must contain the sending organization's name.
                            </li>
                            <li>
                              Must specify the types of messages being sent. If
                              donations are included, must state, “donation
                              asks”.
                            </li>
                            <li>
                              Must disclosure approximate message frequency (Msg
                              frequency varies, 2/msgs per week, etc.)
                            </li>
                            <li>
                              Must include a "Message and data rates may apply"
                              disclosure
                            </li>
                            <li>
                              Must include HELP information (text HELP for help,
                              email or phone number for support)
                            </li>
                            <li>
                              Must include STOP/opt-out information (text STOP
                              to stop)
                            </li>
                            <li>Must include a link to the Privacy Policy</li>
                          </ul>
                          <FormikTextField
                            fullWidth
                            label="Your Terms and Conditions Page"
                            name="termsAndConditionsUrl"
                            disabled={isReadOnly}
                          />
                        </Box>

                        <Box>
                          <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                            Privacy Policy Requirements
                          </Typography>
                          <Box>
                            Carriers are strict about how mobile user data is
                            handled.
                          </Box>
                          <Box>
                            <ul>
                              <li>
                                You can only share mobile data for essential
                                services or if required by law.
                              </li>
                              <li>
                                You cannot share mobile data with affiliates or
                                similar campaigns.
                              </li>
                            </ul>
                          </Box>
                          <FlexBox direction="column" gap={2}>
                            <Box>
                              Your privacy policy must clearly state this.
                            </Box>
                            <Box>
                              You can make this clear on your privacy policy
                              with language such as:
                            </Box>
                            <Box sx={{ fontStyle: "italic", pl: 3 }}>
                              Text messaging originator opt in data and consent
                              will not be shared with any third parties,
                              provided that the foregoing does not apply to
                              sharing (1) with vendors, consultants and other
                              service providers who need access to such
                              information to carry out work on our behalf (and
                              who will not use such information for their own
                              purposes); (2) if we believe disclosure is
                              required by any applicable law, rule, or
                              regulation or to comply with law enforcement or
                              legal process.
                            </Box>
                            <FormikTextField
                              fullWidth
                              label="Privacy Policy Page"
                              name="privacyPolicyUrl"
                              disabled={isReadOnly}
                            />
                          </FlexBox>
                        </Box>

                        <Box>
                          <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                            Opt In Form Requirements
                          </Typography>

                          <Box>
                            <Box>
                              In order to qualify for toll-free, our vendor
                              wants to see that you have an SMS opt in page. It
                              is critical that your opt in page have these
                              things:
                            </Box>

                            <Box>
                              <ul>
                                <li>
                                  Must have an optional field to enter a phone
                                  number.
                                </li>
                                <li>
                                  Must have an optional checkbox for the user to
                                  consent to SMS texts. Cannot have a
                                  pre-checked checkbox signing users up for
                                  texts.
                                </li>
                                <li>
                                  Must include a call-to-action explaining to
                                  the user they are signing up for texts.
                                </li>
                                <li>
                                  Must link to the terms and conditions and
                                  privacy policy.
                                </li>
                                <li>
                                  Eg. "By submitting this form and signing up
                                  for texts, you consent to receive marketing
                                  text messages (e.g. promos, cart reminders)
                                  from [Company Name] at the number provided,
                                  including messages sent by autodialer. Consent
                                  is not a condition of purchase. Msg & data
                                  rates may apply. Msg frequency varies.
                                  Unsubscribe at any time by replying STOP or
                                  clicking the unsubscribe link (where
                                  available). Reply HELP for help. Privacy
                                  Policy [link] & Terms [link]."
                                </li>
                              </ul>
                            </Box>

                            <FormikTextField
                              name="optInPageUrl"
                              label="Opt In Page"
                              fast
                              fullWidth
                              disabled={isReadOnly}
                            />
                          </Box>
                        </Box>
                      </FlexBox>
                    </CollapsibleDrawer>
                  </Box>
                )}

                <Divider sx={{ pt: 2, mb: 3 }} />
                <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                  What kinds of messages will you send?
                </Typography>

                <Typography variant="display2" sx={{ pb: 2 }}>
                  Please describe your use case and the purpose of your messages
                  (i.e. political, fundraising, event reminders, etc.)
                </Typography>

                <InputRow>
                  <FormikTextField
                    multiline
                    name="useCaseSummary"
                    label="Description"
                    fast
                    disabled={isReadOnly}
                  />
                </InputRow>

                <FlexBox direction="column" gap={1}>
                  <Typography variant="display2">
                    Please provide 1-3 sample scripts. Your samples must include
                    the sender org name and opt out language, i.e. Stop to opt
                    out
                  </Typography>
                  <FormikTextField
                    name="productionMessageContent"
                    label="Sample messages"
                    fast
                    fullWidth
                    multiline
                    disabled={isReadOnly}
                  />
                </FlexBox>

                <InputRow>
                  <Box>
                    <Typography
                      variant="display2"
                      component="div"
                      sx={{ pb: 1 }}
                    >
                      Where will you use these toll-free numbers?
                    </Typography>
                    <FormGroup row>
                      {COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS.map(
                        option => (
                          <FormControlLabel
                            key={option.label}
                            control={
                              <Checkbox
                                name={`usageType-${option.value}`}
                                checked={values.usageTypes.includes(
                                  option.value
                                )}
                                disabled={isReadOnly}
                                onChange={async e => {
                                  if (e.target.checked) {
                                    await setFieldValue("usageTypes", [
                                      ...values.usageTypes,
                                      option.value,
                                    ]);
                                  } else {
                                    await setFieldValue(
                                      "usageTypes",
                                      values.usageTypes.filter(
                                        (
                                          type: ComplianceRegistrationUsageType
                                        ) => type !== option.value
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={option.label}
                          />
                        )
                      )}
                    </FormGroup>
                    {touched.usageTypes && errors.usageTypes && (
                      <Typography sx={{ color: palette.red }}>
                        {errors.usageTypes as string}
                      </Typography>
                    )}
                  </Box>

                  <FormikSelect
                    label="Expected Texts per Month"
                    name="messageVolume"
                    withNoneOption
                    noneOptionLabel="Expected texts..."
                    fast
                    disabled={isReadOnly}
                  >
                    {[
                      100, 1000, 10000, 100000, 250000, 500000, 750000, 1000000,
                      5000000, 10000000,
                    ].map(n => (
                      <MenuItem key={n} value={n}>
                        {n.toLocaleString()}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                  <div />
                </InputRow>

                <Divider sx={{ pt: 2, mb: 3 }} />

                <FlexBox gap={1} direction="column">
                  <Typography variant="h2SemiBold" sx={{ pb: 1 }}>
                    Contact Details
                  </Typography>

                  <Typography variant="display2">
                    Use a contact from the organization sending the texts. For
                    example, if the texts are from "Jane's campaign," the
                    contact should be someone from that campaign. This is just
                    to confirm the group's identity. If the person Scale to Win
                    should contact with questions isn't from the organization,
                    add them in the “Primary Contact” section below.
                  </Typography>
                  <Typography variant="display2" sx={{ pb: 1 }}>
                    Important: The email should match the organization's website
                    domain. Gmail can work only if the org's name is clearly
                    mentioned somewhere.
                  </Typography>

                  <InputRow>
                    <FormikTextField
                      label="Organization Contact First Name"
                      name="contactFirstName"
                      fast
                      disabled={isReadOnly}
                    />
                    <FormikTextField
                      label="Organization Contact Last Name"
                      name="contactLastName"
                      fast
                      disabled={isReadOnly}
                    />
                  </InputRow>

                  <InputRow>
                    <FormikTextField
                      label="Organization Contact Email"
                      name="contactEmail"
                      fast
                      disabled={isReadOnly}
                    />

                    <FormikTextField
                      label="Organization Contact Phone"
                      name="contactPhone"
                      fast
                      disabled={isReadOnly}
                    />
                  </InputRow>

                  <InputRow>
                    <Typography variant="display2">
                      Is the organization contact the person Scale to Win should
                      reach out to with questions about this registration?
                    </Typography>

                    <RadioGroup
                      row={true}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                      }}
                      value={values.orgContactIsPrimaryContact}
                      onChange={async e => {
                        await setFieldValue(
                          "orgContactIsPrimaryContact",
                          e.target.value === "true"
                        );
                      }}
                      name="orgContactIsPrimaryContact"
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Yes"
                        disabled={isReadOnly}
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                        disabled={isReadOnly}
                      />
                    </RadioGroup>
                  </InputRow>
                  {values.orgContactIsPrimaryContact ? null : (
                    <>
                      <InputRow>
                        <FormikTextField
                          label="Primary Contact First Name"
                          name="primaryContactFirstName"
                          fast
                          disabled={isReadOnly}
                        />
                        <FormikTextField
                          label="Primary Contact Last Name"
                          name="primaryContactLastName"
                          fast
                          disabled={isReadOnly}
                        />
                      </InputRow>
                      <InputRow>
                        <FormikTextField
                          label="Primary Contact Email"
                          name="primaryContactEmail"
                          fast
                          disabled={isReadOnly}
                        />

                        <FormikTextField
                          label="Primary Contact Phone"
                          name="primaryContactPhone"
                          fast
                          disabled={isReadOnly}
                        />
                      </InputRow>
                    </>
                  )}
                  <InputRow>
                    <FormikTextField
                      multiline
                      label="Anything you would like to share with Scale to Win staff? (optional)"
                      name="notes"
                      fast
                      disabled={isReadOnly}
                    />
                  </InputRow>
                </FlexBox>

                <FlexBox
                  gap={2}
                  justifyContent="flex-end"
                  sx={{ mt: 1, display: isReadOnly ? "none" : "flex" }}
                >
                  <LinkButton
                    onClick={() => {
                      setTestPreset("default");
                      resetForm();
                    }}
                    disabled={isSavingDraft || isSubmittingToStaff || !dirty}
                  >
                    Reset Form
                  </LinkButton>
                  {subaccount && (
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={isSavingDraft || isSubmittingToStaff || !dirty}
                      onClick={saveDraft}
                    >
                      Save Draft
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="contained"
                    disabled={
                      isSavingDraft ||
                      isSubmittingToStaff ||
                      (allowsDraftSave && dirty) // Save draft is enabled and the form has unsaved input
                    }
                    onClick={submitToStaff}
                  >
                    Submit for Review
                  </Button>
                </FlexBox>
              </>
            );
          }}
        </Formik>
      </AccentBox>
    </PageContainer>
  );
};

export default function TollFreeRegistration(): ReactElement {
  const { tollFreeRegistrationId } = useParams<{
    tollFreeRegistrationId: string;
  }>();

  const { subaccount } = useSubaccount()!;

  const { data, loading, error } = useQuery<
    GetTollFreeRegistrationForAdminQuery,
    GetTollFreeRegistrationForAdminQueryVariables
  >(GET_TOLL_FREE_REGISTRATION_FOR_ADMIN, {
    variables: {
      input: {
        registrationId: tollFreeRegistrationId!,
        subaccountId: subaccount.id,
      },
    },
    skip: !tollFreeRegistrationId,
  });

  const [submitTollFreeRegistrationToStaff] = useMutation<
    SubmitTollFreeRegistrationToStaffMutation,
    SubmitTollFreeRegistrationToStaffMutationVariables
  >(SUBMIT_TOLL_FREE_REGISTRATION_TO_STAFF, {
    onErrorSnackbarMessage: "Failed to submit toll free registration to staff",
  });

  async function handleSubmitToStaff(
    input: Omit<
      SubmitTollFreeRegistrationToStaffMutationVariables["input"],
      "subaccountId"
    >
  ): Promise<{ errors: Error[] | string[] | undefined | null }> {
    const { errors } = await submitTollFreeRegistrationToStaff({
      variables: {
        input: {
          ...input,
          subaccountId: subaccount.id,
        },
      },
    });
    if (errors) {
      return { errors: errors as unknown as string[] };
    }
    return { errors: null };
  }

  if (tollFreeRegistrationId) {
    return (
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ getTollFreeRegistrationForAdmin }) => (
          <InternalTollFreeRegistrationForm
            registration={getTollFreeRegistrationForAdmin ?? undefined}
            handleSubmitToStaff={handleSubmitToStaff}
          />
        )}
      </AsyncWrapper>
    );
  }
  return (
    <InternalTollFreeRegistrationForm
      handleSubmitToStaff={handleSubmitToStaff}
    />
  );
}
